import { Component, OnInit, Input, Output, OnChanges, SimpleChanges, EventEmitter } from '@angular/core';
import { ToasterService } from 'src/app/toaster.service';
import { Toaster, ToasterType } from 'src/app/shared/types';
import { HostAdjustmentService } from './host-adjustment.service';
import { AppConfigService } from '../appConfig.service';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';

@Component({
  selector: 'app-host-adjustment',
  templateUrl: './host-adjustment.component.html',
  styleUrls: ['./host-adjustment.component.css']
})
export class HostAdjustmentComponent implements OnInit, OnChanges {

  @Input() hostAdjDetails: any;
  @Output() adjustmentDone: EventEmitter<any> = new EventEmitter<any>();

  public disableSubmit = false;
  public success = false;
  public statusValue = [];
  public refundReasons = [];
  public debitReasons = [];
  public maxCreditLimit = 0;
  public maxDebitLimit = 0;

  public info: any = {
    header: 'Captain Wallet Adjustment',
    headerClass: null,
    dataFetched: true,
    rows: []
  };

  private roles: string[] = [];
  private configLoaded = false;

  constructor(
    private toasterService: ToasterService,
    private hostAdjustmentService: HostAdjustmentService,
    private appConfigService: AppConfigService,
    public dialog: MatDialog
  ) {}

  async ngOnInit() {
    this.roles = this.accessCookie('Roles').split(',');
    try {
      const config: any = await this.appConfigService.getPoolOrdersAdjustmentConfig();
      const creditLimitMap = config.creditLimit || {};
      const debitLimitMap = config.debitLimit || {};
      this.refundReasons = config.credit_reasons ? JSON.parse(config.credit_reasons) : [];
      this.debitReasons = config.debit_reasons ? JSON.parse(config.debit_reasons) : [];

      this.maxCreditLimit = this.resolveLimit(creditLimitMap);
      this.maxDebitLimit = this.resolveLimit(debitLimitMap);
    } catch (e) {
      console.error('Failed to load hopr adjustment config', e);
    }
    this.configLoaded = true;
    this.buildActions();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes && changes.hostAdjDetails && changes.hostAdjDetails.currentValue) {
      if (!changes.hostAdjDetails.currentValue.dataFetched) {
        this.emptyCard('Cannot load adjustment');
        return;
      }
    }
    if (!this.configLoaded) { return; }
    this.buildActions();
  }

  private resolveLimit(limitMap: Record<string, number>): number {
    const limits = this.roles
      .filter(role => limitMap[role] !== undefined)
      .map(role => limitMap[role]);
    return limits.length > 0 ? Math.max(...limits) : 0;
  }

  private buildActions() {
    if (this.maxCreditLimit > 0) {
      if (!this.statusValue.includes('Refund')) { this.statusValue.push('Refund'); }
    }
    if (this.maxDebitLimit > 0) {
      if (!this.statusValue.includes('Debit')) { this.statusValue.push('Debit'); }
    }
    this.constructData();
  }

  constructData() {
    if (this.statusValue.length > 0) {
      this.info = {
        header: 'Captain Wallet Adjustment',
        headerClass: null,
        dataFetched: true,
        rows: [
          [
            { titleClass: 'form-title-class', title: 'Action', type: 'single-searchable-dropdown',
              values: this.statusValue, placeholder: 'Select action', key: 'hopr-host-action',
              default: undefined, className: 'col-md-6' },
            { titleClass: 'form-title-class', title: 'Reason', type: 'single-searchable-dropdown',
              values: [], placeholder: 'Select reason', key: 'hopr-host-reason', className: 'col-md-6' }
          ],
          [
          { titleClass: 'form-title-class', title: 'Amount', type: 'text-input',
            placeholder: 'Enter Amount', key: 'hopr-host-amount', className: 'col-md-6', numericOnly: true },
          { titleClass: 'form-title-class', title: 'Wallet', type: 'single-searchable-dropdown',
            values: ['Host Wallet'], placeholder: 'Host Wallet', key: 'hopr-host-wallet',
            className: 'col-md-6', default: 'Host Wallet' }
        ]
      ]
    };
    } else {
      this.info = {
        header: 'Captain Wallet Adjustment',
        accessDenied: true,
        dataFetched: true
      };
    }
  }

  changeRows(event: string) {
    const reasons = event === 'Refund' ? this.refundReasons : this.debitReasons;
    if (event === 'Refund' || event === 'Debit') {
      this.info = {
        header: 'Captain Wallet Adjustment',
        headerClass: null,
        dataFetched: true,
        rows: [
          [
            { titleClass: 'form-title-class', title: 'Action', type: 'single-searchable-dropdown',
              values: this.statusValue, placeholder: 'Select action', key: 'hopr-host-action',
              default: event, className: 'col-md-6' },
            { titleClass: 'form-title-class', title: 'Reason', type: 'single-searchable-dropdown',
              values: reasons, placeholder: 'Select reason', key: 'hopr-host-reason', className: 'col-md-6' }
          ],
          [
            { titleClass: 'form-title-class', title: 'Amount', type: 'text-input',
              placeholder: 'Enter Amount', key: 'hopr-host-amount', className: 'col-md-6', numericOnly: true },
            { titleClass: 'form-title-class', title: 'Wallet', type: 'single-searchable-dropdown',
              values: ['Host Wallet'], placeholder: 'Host Wallet', key: 'hopr-host-wallet',
              className: 'col-md-6', default: 'Host Wallet' }
          ]
        ]
      };
    }
  }

  async performAdjustment(event: any) {
    const action = event['hopr-host-action'];
    const reason = event['hopr-host-reason'];
    const amount = Number(event['hopr-host-amount']);

    if (!action) {
      this.showWarning('Please select action'); return;
    }
    if (!reason) {
      this.showWarning('Please select reason'); return;
    }
    if (!amount || amount <= 0) {
      this.showWarning('Please enter a valid amount'); return;
    }
    if (!(parseFloat(event['hopr-host-amount']) == event['hopr-host-amount'])) {
      this.showWarning('Please enter a valid number'); return;
    }

    const maxLimit = action === 'Refund' ? this.maxCreditLimit : this.maxDebitLimit;
    if (amount > maxLimit) {
      this.showWarning(`You are allowed to ${action} max Rs. ${maxLimit}`); return;
    }

    const userDetails = JSON.parse(this.accessCookie('user'));
    const orderId = this.hostAdjDetails.orderId;

    const adjustmentObj = {
      orderDate: this.hostAdjDetails.orderDate || '',
      riderId: this.hostAdjDetails.rider || '',
      city: this.hostAdjDetails.city && this.hostAdjDetails.city._id || '',
      uniqueId: this.hostAdjDetails.uniqueId || '',
      remarks: reason,
      adjustedBy: userDetails['emailId'],
      adjustedById: userDetails['_id'],
      amount: action === 'Debit' ? -Math.abs(amount) : amount,
      orderId,
      mobile: this.hostAdjDetails.riderObj && this.hostAdjDetails.riderObj.mobile || '',
      name: this.hostAdjDetails.riderObj && this.hostAdjDetails.riderObj.name || '',
      serviceDetail: this.hostAdjDetails.serviceDetail,
      serviceId: this.hostAdjDetails.serviceId,
      service: this.hostAdjDetails.service,
      orderCreatedOnEpoch: this.hostAdjDetails.orderCreatedOn
    };

    let role = '';
    if (this.roles.includes('CCC Admin')) { role = 'CCC Admin'; }
    else if (this.roles.includes('Captain care centre agents')) { role = 'Captain care centre agents'; }

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '335px',
      data: {
        title: `Are you sure you want to ${action}?`,
        type: 'order-details',
        reason: action,
        message: '',
        buttonText: `Yes, ${action}`
      }
    });

    dialogRef.afterClosed().subscribe(confirmed => {
      if (!confirmed) { return; }
      this.disableSubmit = true;

      if (action === 'Refund') {
        this.hostAdjustmentService.hostRefundDetails(orderId, adjustmentObj, role).subscribe(
          response => {
            if (response && response['info'] && response['info'].status === 'success') {
              this.showSuccess('Amount refunded successfully');
              this.adjustmentDone.emit();
            }
            this.disableSubmit = false;
          },
          err => {
            this.disableSubmit = false;
            this.showWarning(err && err.error && err.error.message || 'Unable to refund amount');
          }
        );
      } else {
        this.hostAdjustmentService.hostDebitDetails(orderId, adjustmentObj).subscribe(
          response => {
            if (response && response['info'] && response['info'].status === 'success') {
              this.showSuccess('Amount debited successfully');
              this.adjustmentDone.emit();
            }
            this.disableSubmit = false;
          },
          err => {
            this.disableSubmit = false;
            this.showWarning(err && err.error && err.error.message || 'Unable to debit amount');
          }
        );
      }
    });
  }

  private showWarning(message: string) {
    this.toasterService.showToaster(new Toaster({ type: ToasterType.WARNING, message }));
  }

  private showSuccess(message: string) {
    this.toasterService.showToaster(new Toaster({ type: ToasterType.SUCCESS, message }));
    this.success = true;
  }

  private emptyCard(message?: string) {
    this.info = {
      header: 'Captain Wallet Adjustment',
      dataFetched: false,
      emptyMessage: message || 'No data available'
    };
  }

  private accessCookie(cookieName: string): string {
    const name = cookieName + '=';
    const allCookieArray = document.cookie.split(';');
    for (let i = 0; i < allCookieArray.length; i++) {
      if (allCookieArray[i]) {
        const cookieItem = allCookieArray[i].trim();
        if (cookieItem.indexOf(name) === 0) {
          return cookieItem.substring(name.length, cookieItem.length);
        }
      }
    }
    return '';
  }
}
